import React, { ComponentProps, useRef } from 'react'
import { mergeProps, useButton } from 'react-aria'
import { SvgAdd, SvgDropdown } from '@chilipiper/icons/src/design-system'
import styled from 'styled-components'
import { AriaProps, OmitInternalProperties, PositioningProps } from '../../types'
import { Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import {
  BaseButtonBox,
  BaseButtonEndIcon,
  BaseButtonIcon,
  BaseButtonVariant,
  getCommonProps,
} from '../base-button/BaseButton'
import { ThemeColors, ThemeShadows } from '../../../theme'
import { Popover } from '../../../core-components/popover/Popover'
import { splitAriaProps } from '../../helpers'

type Props = {
  children: React.ReactNode
  'data-id'?: string
  'data-test-id'?: string
  hasAddIcon?: boolean
  isDisabled?: boolean
  isFullWidth?: boolean
  isOpen?: boolean
  label: string
  onOpenChange?: (isOpen: boolean) => void
  popoverPlacement?: ComponentProps<typeof Popover>['placement']
  variant: BaseButtonVariant
  size?: 7 | 8
} & PositioningProps

export const OptionButtonInternal = ({
  isOpen,
  onOpenChange,
  children,
  isDisabled,
  'data-id': dataId,
  'data-test-id': dataTestId,
  variant,
  label,
  hasAddIcon,
  isFullWidth,
  popoverPlacement = 'bottom',
  size,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'OptionButton')

  return (
    <Popover
      placement={popoverPlacement}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      offset={8}
      trigger={ariaProps => (
        <Button
          data-id={dataId}
          data-test-id={dataTestId}
          variant={variant}
          isDisabled={isDisabled}
          ariaProps={ariaProps}
          hasAddIcon={hasAddIcon}
          isFullWidth={isFullWidth}
          size={size}
          {...positioningProps}
        >
          {label}
        </Button>
      )}
    >
      {children}
    </Popover>
  )
}

type PublicProps = OmitInternalProperties<Props>

export const OptionButtonPublic = (props: PublicProps) => <OptionButtonInternal {...props} />

const Button = ({
  children,
  variant,
  ariaProps,
  isDisabled,
  'data-id': dataId,
  'data-test-id': dataTestId,
  hasAddIcon,
  isFullWidth,
  size,
  ...positioningProps
}: Pick<
  Props,
  'variant' | 'isDisabled' | 'data-id' | 'data-test-id' | 'hasAddIcon' | 'isFullWidth' | 'size'
> &
  PositioningProps & { ariaProps: AriaProps; children: string }) => {
  const { elementRef, domProps, reactAriaProps } = splitAriaProps(ariaProps)
  const fallbackRef = useRef<HTMLButtonElement>(null)
  const ref = elementRef ?? fallbackRef
  const { buttonProps } = useButton({ ...reactAriaProps, isDisabled }, ref)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { color: _, ...buttonPropsWithoutColor } = buttonProps
  const { color, ...baseButtonProps } = getCommonProps({
    variant,
    isFullWidth,
    size,
  })
  const { dropdownIconColor, dropdownIconHoverColor, ...restVariantProps } = variantProps[variant]
  return (
    <StyledBaseButtonBox
      as='button'
      ref={ref}
      data-id={dataId}
      data-test-id={dataTestId}
      {...baseButtonProps}
      {...restVariantProps}
      endIconColor={dropdownIconColor}
      endIconHoverColor={dropdownIconHoverColor}
      {...positioningProps}
      {...mergeProps(buttonPropsWithoutColor, domProps)}
    >
      {hasAddIcon && <BaseButtonIcon icon={SvgAdd} mr={2} w={4} h={4} />}
      <StyledText textStyle='button' color={color}>
        {children}
      </StyledText>
      <BaseButtonEndIcon icon={SvgDropdown} ml={2} w={4} h={4} />
    </StyledBaseButtonBox>
  )
}

const StyledText = styled(Text)``

const StyledBaseButtonBox = styled(BaseButtonBox)<{
  activeBg: ThemeColors
  activeBorder: ThemeShadows
  activeColor: ThemeColors
  activeIconColor: ThemeColors
}>`
  &[aria-expanded='true'] {
    box-shadow: ${({ theme, activeBorder }) => theme.shadows[activeBorder]} !important;
    background: ${({ theme, activeBg }) => theme.colors[activeBg]};

    ${StyledText} {
      color: ${({ theme, activeColor }) => theme.colors[activeColor]};
    }

    ${BaseButtonIcon} {
      color: ${({ theme, activeIconColor }) => theme.colors[activeIconColor]};
    }

    ${BaseButtonEndIcon} {
      color: ${({ theme }) => theme.colors['fg/minor']};
      transform: rotate(180deg);
    }
  }
`

const variantProps: Record<
  BaseButtonVariant,
  {
    activeBg: ThemeColors
    activeBorder: ThemeShadows
    activeColor: ThemeColors
    activeIconColor: ThemeColors
    dropdownIconColor: ThemeColors
    dropdownIconHoverColor: ThemeColors
  }
> = {
  major: {
    dropdownIconColor: 'fg/major-on-color',
    dropdownIconHoverColor: 'fg/major-on-color',
    activeBorder: 'border/action-focused-major',
    activeBg: 'bg/button-major-active',
    activeColor: 'fg/action',
    activeIconColor: 'fg/action',
  },
  minor: {
    dropdownIconColor: 'fg/minor',
    dropdownIconHoverColor: 'fg/minor',
    activeBorder: 'border/action-focused-minor',
    activeBg: 'bg/button-minor-active',
    activeColor: 'fg/moderate',
    activeIconColor: 'fg/moderate',
  },
  moderate: {
    dropdownIconColor: 'fg/minor',
    dropdownIconHoverColor: 'fg/minor',
    activeBorder: 'border/action-focused-minor',
    activeBg: 'bg/button-moderate-active',
    activeColor: 'fg/major',
    activeIconColor: 'fg/major',
  },
  cautionHigh: {
    dropdownIconColor: 'fg/major-on-color',
    dropdownIconHoverColor: 'fg/major-on-color',
    activeBorder: 'border/action-focused-caution',
    activeBg: 'bg/button-caution-active',
    activeColor: 'fg/caution',
    activeIconColor: 'fg/caution',
  },
  cautionLow: {
    dropdownIconColor: 'fg/minor',
    dropdownIconHoverColor: 'fg/major-on-color',
    activeBorder: 'border/action-focused-caution',
    activeBg: 'bg/button-caution-active',
    activeColor: 'fg/caution',
    activeIconColor: 'fg/caution',
  },
}
